import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import type { RootState } from "../../../store/store";
import { getAuthPayload, isAdminFromToken } from "../../../utils/authToken";
import { formatCurrency } from "../../dp/utils/format";
import { ESTOQUE_PAGE } from "../../estoque/routes";
import { SUPERVISOR_HOME_PAGE } from "../../supervisor/routes";

import {
  RgbConfig,
  RgbMovement,
  RgbMovementType,
  RgbPackaging,
  RgbStore,
  RgbSummaryStore,
  deleteRgbMovementApi,
  getRgbConfigApi,
  getRgbMovementsApi,
  getRgbPackagingsApi,
  getRgbStoresApi,
  getRgbSummaryApi,
} from "../api/rgbApi";
import RgbMovementModal from "../components/RgbMovementModal";
import RgbPhotoViewerModal from "../components/RgbPhotoViewerModal";
import { RGB_IMPORT_PAGE, RGB_PACKAGINGS_PAGE, RGB_REPORT_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import {
  MOTIVO_LABELS,
  TYPE_BADGE,
  TYPE_LABELS,
  currentMonth,
  formatDateBr,
  formatInt,
  formatPercent,
  isoToLocalDay,
  monthRange,
  todayIso,
} from "../rgbFormat";
import "./Rgb.css";

const LIMIT = 20;
const STORE_KEY = "rgb.lastStoreId";

/** Loja lembrada entre visitas — conveniência local, falha em silêncio sem storage. */
const readLastStore = (): string | null => {
  try {
    return window.localStorage.getItem(STORE_KEY);
  } catch {
    return null;
  }
};
const writeLastStore = (id: string) => {
  try {
    window.localStorage.setItem(STORE_KEY, id);
  } catch {
    /* sem storage: só não lembra */
  }
};

export default function RgbHomePage() {
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.accessToken);
  const isAdmin = useMemo(() => isAdminFromToken(token), [token]);
  // `/estoque` é hub só do ADMIN; o supervisor volta para a home dele (BUG-012).
  const goBack = useGoBack(isAdmin ? ESTOQUE_PAGE() : SUPERVISOR_HOME_PAGE());
  const myId = useMemo(() => getAuthPayload(token)?.sub ?? null, [token]);

  const [config, setConfig] = useState<RgbConfig | null>(null);
  const [stores, setStores] = useState<RgbStore[]>([]);
  const [packagings, setPackagings] = useState<RgbPackaging[]>([]);
  const [storeId, setStoreId] = useState("");
  const [month, setMonth] = useState(currentMonth());
  const [booting, setBooting] = useState(true);

  const [summary, setSummary] = useState<RgbSummaryStore | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const [movements, setMovements] = useState<RgbMovement[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<RgbMovementType | "">("");
  const [loadingList, setLoadingList] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewer, setViewer] = useState<RgbMovement | null>(null);

  const store = useMemo(() => stores.find((s) => s.id === storeId) ?? null, [stores, storeId]);
  const range = useMemo(() => monthRange(month), [month]);
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  // Carga inicial: configuração efetiva, lojas acessíveis e catálogo ativo.
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [cfg, st, pk] = await Promise.all([getRgbConfigApi(), getRgbStoresApi(), getRgbPackagingsApi()]);
        setConfig(cfg);
        setStores(st);
        setPackagings(pk);
        const remembered = readLastStore();
        const initial = st.find((s) => s.id === remembered) ?? (st.length === 1 ? st[0] : null);
        if (initial) setStoreId(initial.id);
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Erro ao carregar o módulo.");
      } finally {
        setBooting(false);
      }
    })();
  }, [token]);

  const loadSummary = useCallback(async () => {
    if (!storeId) return;
    setLoadingSummary(true);
    try {
      const data = await getRgbSummaryApi({ ...range, storeIds: [storeId] });
      setSummary(data.lojas[0] ?? null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao carregar o resumo.");
    } finally {
      setLoadingSummary(false);
    }
  }, [storeId, range]);

  const loadList = useCallback(async () => {
    if (!storeId) return;
    setLoadingList(true);
    try {
      const data = await getRgbMovementsApi({
        storeIds: [storeId],
        ...range,
        type: typeFilter || undefined,
        page,
        limit: LIMIT,
      });
      setMovements(data.items);
      setTotal(data.total);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao listar os apontamentos.");
    } finally {
      setLoadingList(false);
    }
  }, [storeId, range, typeFilter, page]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const reloadAll = () => {
    loadSummary();
    if (page !== 1) setPage(1);
    else loadList();
  };

  const canDelete = (m: RgbMovement) =>
    isAdmin || (m.createdBy.id === myId && !m.importBatchId && isoToLocalDay(m.createdAt) === todayIso());

  const remove = async (m: RgbMovement) => {
    if (!window.confirm(`Excluir o apontamento de ${m.quantidade} × ${m.packaging.nome}?`)) return;
    try {
      await deleteRgbMovementApi(m.id);
      toast.success("Apontamento excluído.");
      reloadAll();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Erro ao excluir.");
    }
  };

  const metaBadge = () => {
    if (!config) return null;
    if (summary?.acimaDaMetaMensal || summary?.acimaDaMetaPercentual) {
      return <span className="rgbBadge rgbBadge--red">Acima da meta</span>;
    }
    if (config.metaRefugoMensalPorLoja === null && config.metaRefugoPercentual === null) {
      return <span className="rgbBadge rgbBadge--gray">Sem meta definida</span>;
    }
    return <span className="rgbBadge rgbBadge--green">Dentro da meta</span>;
  };

  if (!token) return null;

  return (
    <div className="rgbLayout">
      <main className="rgbMain">
        <div className="rgbContainer">
          <EcoPageHeader
            crumbs={[...(isAdmin ? [{ label: "Estoque", onClick: () => navigate(ESTOQUE_PAGE()) }] : [{ label: "Estoque" }]), { label: "Vasilhame RGB" }]}
            title="Vasilhame RGB"
            subtitle={
              config?.controleSaldo
                ? "Refugo, recebimento, devolução ao CDD e saldo de vasilhame retornável por loja."
                : "Apontamento de refugo de vasilhame retornável por loja."
            }
            onBack={goBack}
            backLabel="Voltar"
            actions={
              <>
                {isAdmin ? (
                  <>
                    <button type="button" className="ecoBtn" onClick={() => navigate(RGB_REPORT_PAGE())}>
                      <EcoIcon name="barChart" size={18} />
                      Relatório
                    </button>
                    <button type="button" className="ecoBtn" onClick={() => navigate(RGB_PACKAGINGS_PAGE())}>
                      <EcoIcon name="box" size={18} />
                      Vasilhames
                    </button>
                  </>
                ) : null}
                <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => navigate(RGB_IMPORT_PAGE())}>
                  <EcoIcon name="download" size={18} />
                  Importar planilha
                </button>
              </>
            }
          />

          {booting ? (
            <p className="rgbEmpty">Carregando...</p>
          ) : stores.length === 0 ? (
            <p className="rgbEmpty">Nenhuma loja vinculada ao seu usuário.</p>
          ) : (
            <>
              <div className="rgbControls">
                <div className="rgbFormGroup">
                  <label htmlFor="rgbStore">Loja</label>
                  <select
                    id="rgbStore"
                    className="rgbSelect"
                    value={storeId}
                    onChange={(e) => {
                      setStoreId(e.target.value);
                      setPage(1);
                      if (e.target.value) writeLastStore(e.target.value);
                    }}
                  >
                    <option value="">Selecione a loja...</option>
                    {stores.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.nomeFantasia}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="rgbFormGroup">
                  <label htmlFor="rgbMonth">Mês</label>
                  <input
                    id="rgbMonth"
                    className="rgbInput"
                    type="month"
                    max={currentMonth()}
                    value={month}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      setMonth(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
                <button
                  className="rgbBtn rgbBtn--primary"
                  disabled={!store || !config || packagings.length === 0}
                  onClick={() => setIsModalOpen(true)}
                >
                  Novo apontamento
                </button>
              </div>

              {!store ? (
                <p className="rgbEmpty">Selecione uma loja para ver o resumo e os apontamentos.</p>
              ) : (
                <>
                  <section className="rgbSection">
                    <div className="rgbStoreSummary__head">
                      <h2 className="rgbSection__title" style={{ margin: 0 }}>
                        Resumo do mês
                      </h2>
                      <div className="rgbStoreSummary__totals">
                        <span>
                          Refugado: <strong>{formatInt(summary?.refugadoTotal ?? 0)}</strong>
                        </span>
                        <span>
                          Perda estimada: <strong>{formatCurrency(summary?.perdaEstimadaTotal)}</strong>
                        </span>
                        {config?.controleSaldo ? (
                          <span>
                            % refugo: <strong>{formatPercent(summary?.percentualRefugo)}</strong>
                          </span>
                        ) : null}
                        {metaBadge()}
                      </div>
                    </div>

                    {loadingSummary ? (
                      <p className="rgbEmpty">Carregando...</p>
                    ) : !summary ? (
                      <p className="rgbEmpty">Nenhum apontamento neste mês.</p>
                    ) : (
                      <div className="rgbSummaryGrid">
                        {summary.itens.map((i) => (
                          <div key={i.packaging.id} className="rgbSummaryCard">
                            <div className="rgbSummaryCard__title">{i.packaging.nome}</div>
                            <dl className="rgbSummaryCard__rows">
                              {config?.controleSaldo ? (
                                <>
                                  <dt>Recebido</dt>
                                  <dd>{formatInt(i.recebido)}</dd>
                                  <dt>Devolvido ao CDD</dt>
                                  <dd>{formatInt(i.devolvido)}</dd>
                                </>
                              ) : null}
                              <dt className={i.refugado > 0 ? "is-strong" : undefined}>Refugado</dt>
                              <dd className={i.refugado > 0 ? "is-strong" : undefined}>{formatInt(i.refugado)}</dd>
                              {config?.controleSaldo ? (
                                <>
                                  <dt>Ajustes</dt>
                                  <dd>{formatInt(i.ajustes)}</dd>
                                  <dt>Saldo</dt>
                                  <dd>{formatInt(i.saldo)}</dd>
                                </>
                              ) : null}
                              <dt>Perda estimada</dt>
                              <dd>{formatCurrency(i.perdaEstimada)}</dd>
                            </dl>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>

                  <section className="rgbSection">
                    <div className="rgbStoreSummary__head">
                      <h2 className="rgbSection__title" style={{ margin: 0 }}>
                        Apontamentos
                      </h2>
                      <select
                        className="rgbSelect"
                        style={{ width: "auto", minWidth: 180 }}
                        value={typeFilter}
                        onChange={(e) => {
                          setTypeFilter(e.target.value as RgbMovementType | "");
                          setPage(1);
                        }}
                        aria-label="Filtrar por tipo"
                      >
                        <option value="">Todos os tipos</option>
                        {(Object.keys(TYPE_LABELS) as RgbMovementType[])
                          .filter((t) => config?.controleSaldo || t === "REFUGO")
                          .map((t) => (
                            <option key={t} value={t}>
                              {TYPE_LABELS[t]}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="rgbTableWrapper">
                      <table className="rgbTable">
                        <thead>
                          <tr>
                            <th>Data</th>
                            <th>Tipo</th>
                            <th>Vasilhame</th>
                            <th className="is-num">Qtd</th>
                            <th>Motivo</th>
                            <th>Observação</th>
                            <th>Fotos</th>
                            <th>Origem</th>
                            <th>Autor</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {loadingList ? (
                            <tr>
                              <td colSpan={10} className="rgbEmpty">
                                Carregando...
                              </td>
                            </tr>
                          ) : movements.length === 0 ? (
                            <tr>
                              <td colSpan={10} className="rgbEmpty">
                                Nenhum apontamento no filtro.
                              </td>
                            </tr>
                          ) : (
                            movements.map((m) => (
                              <tr key={m.id}>
                                <td>{formatDateBr(m.data)}</td>
                                <td>
                                  <span className={`rgbBadge ${TYPE_BADGE[m.type]}`}>{TYPE_LABELS[m.type]}</span>
                                </td>
                                <td>{m.packaging.nome}</td>
                                <td className="is-num">{formatInt(m.quantidade)}</td>
                                <td>{m.motivo ? MOTIVO_LABELS[m.motivo] : <span className="rgbMuted">—</span>}</td>
                                <td style={{ maxWidth: 240 }}>
                                  {m.observacao || <span className="rgbMuted">—</span>}
                                </td>
                                <td>
                                  {m.fotoAssetIds.length ? (
                                    <button type="button" className="rgbPhotoBtn" onClick={() => setViewer(m)}>
                                      Ver ({m.fotoAssetIds.length})
                                    </button>
                                  ) : (
                                    <span className="rgbMuted">—</span>
                                  )}
                                </td>
                                <td>{m.source === "FILE" ? "Planilha" : "Manual"}</td>
                                <td>{m.createdBy.nomeCompleto}</td>
                                <td>
                                  {canDelete(m) ? (
                                    <button className="rgbBtn rgbBtn--danger rgbBtn--small" onClick={() => remove(m)}>
                                      Excluir
                                    </button>
                                  ) : null}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="rgbPagination">
                      <span>
                        Mostrando {movements.length} de {total} apontamento(s)
                      </span>
                      <div className="rgbPagination__btns">
                        <button
                          className="rgbBtn rgbBtn--secondary"
                          style={{ height: 32 }}
                          disabled={page <= 1 || loadingList}
                          onClick={() => setPage((p) => p - 1)}
                        >
                          Anterior
                        </button>
                        <span style={{ padding: "0 12px" }}>
                          Página {page} de {totalPages}
                        </span>
                        <button
                          className="rgbBtn rgbBtn--secondary"
                          style={{ height: 32 }}
                          disabled={page >= totalPages || loadingList}
                          onClick={() => setPage((p) => p + 1)}
                        >
                          Próxima
                        </button>
                      </div>
                    </div>
                  </section>
                </>
              )}
            </>
          )}
        </div>
      </main>

      {config ? (
        <RgbMovementModal
          isOpen={isModalOpen}
          token={token}
          config={config}
          store={store}
          packagings={packagings}
          onClose={() => setIsModalOpen(false)}
          onCreated={reloadAll}
        />
      ) : null}

      <RgbPhotoViewerModal
        token={token}
        assetIds={viewer?.fotoAssetIds ?? null}
        title={viewer ? `${viewer.packaging.nome} — ${formatDateBr(viewer.data)}` : ""}
        onClose={() => setViewer(null)}
      />
    </div>
  );
}
